import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  HelpCircle,
  MessageCircle,
  Mail,
  Phone,
  Search,
  Rocket,
  BookOpen,
  RotateCcw,
} from 'lucide-react';
import { useState } from 'react';
import { useTour } from '../contexts/TourContext';
import { useMenuSettings } from '../contexts/MenuSettingsContext';
import { useAuth } from '../contexts/AuthContext';
import { useQualityMeasureLabel } from '../lib/qualityLabels';

type FaqSlug =
  | 'getting_started'
  | 'onboarding'
  | 'questionnaires'
  | 'purchasing'
  | 'supplier_management'
  | 'documents_communication';
type FaqAudience = 'customer' | 'supplier' | 'both';
interface FaqItem { question: string; answer: string; audience: FaqAudience }

// Stable slugs match backend PlatformSettings.help_layout.faq keys so a
// superuser can hide any category for a tenant whose workflow doesn't need it
// (e.g. MAB Compliance hides `purchasing`). Each item is additionally tagged
// with an audience so supplier users see supplier-relevant answers and
// customer users see customer-relevant ones — an earlier pass lumped both
// together which meant e.g. a supplier reading the FAQ saw "How do I add a
// new supplier" and a customer saw "How do I acknowledge a PO" (neither
// action they can take).
// Built as a function so the quality-measure label ('PPM' / 'PPK') can be
// interpolated from the platform toggle without baking it into a literal.
const buildFaqItems = (qualityLabel: string): Array<{ slug: FaqSlug; category: string; items: FaqItem[] }> => [
  {
    slug: 'getting_started',
    category: 'Getting Started',
    items: [
      {
        audience: 'both',
        question: 'How do I navigate the platform?',
        answer: 'Use the sidebar menu on the left to open each module available to your tenant. The Dashboard shows at-a-glance metrics for the modules you use; open any chart to drill into the underlying records.',
      },
      {
        audience: 'both',
        question: 'How do I update my profile information?',
        answer: 'Open the Settings page from the user menu in the top-right. In the Profile tab, update your name, contact details and notification preferences.',
      },
      {
        audience: 'both',
        question: 'How do I change my password?',
        answer: 'Open Settings > Security and click "Change Password". You will need to enter your current password to set a new one.',
      },
    ],
  },
  {
    slug: 'onboarding',
    category: 'Onboarding',
    items: [
      // Customer-side
      {
        audience: 'customer',
        question: 'How do I invite a new supplier?',
        answer: 'Open Supplier Onboarding > Add Supplier. Enter the supplier\'s company name and a contact email — they receive an email invitation to register and complete their onboarding questionnaires.',
      },
      {
        audience: 'customer',
        question: 'How do I approve, decline, or request more information on a supplier?',
        answer: 'Open Supplier Onboarding and pick a submitted workflow. The Approval Wizard walks through each questionnaire. On the review screen you can Approve, Decline, or Request More Information — the supplier is notified of the outcome.',
      },
      {
        audience: 'customer',
        question: 'What do the onboarding statuses mean?',
        answer: 'Invited — the supplier has been emailed but hasn\'t started. In Progress / Questionnaires Pending — the supplier is filling them in. Submitted / Under Review — ready for you to approve. More Info Required — you\'ve sent it back with a note. Approved / Declined — final state.',
      },
      {
        audience: 'customer',
        question: 'How do I track suppliers approaching renewal?',
        answer: 'The Compliance dashboard includes an "Approvals Expiring" chart, bucketed by 30/60/90 days. You can also filter the Supplier Onboarding list by expiry date.',
      },
      // Supplier-side
      {
        audience: 'supplier',
        question: 'How do I complete my onboarding?',
        answer: 'Open My Onboarding from the sidebar. Each outstanding questionnaire appears in the list — click one, complete every required question, Save Progress as you go, and submit when you are done.',
      },
      {
        audience: 'supplier',
        question: 'How do I submit my onboarding for review?',
        answer: 'Once every assigned questionnaire shows 100% complete, click "Submit for Approval" on the My Onboarding page. Your customer is notified and reviews your answers.',
      },
      {
        audience: 'supplier',
        question: 'What happens after I submit?',
        answer: 'Your customer receives a notification and reviews your questionnaires. You will be notified when they Approve, Decline, or Request More Information. If they ask for more information, the questionnaire re-opens for you to amend.',
      },
      {
        audience: 'supplier',
        question: 'When does my approval expire?',
        answer: 'Approvals typically last 12 months from the date of approval. Your customer will contact you before expiry to renew — your previous answers are carried forward so you only need to confirm or update them.',
      },
    ],
  },
  {
    slug: 'questionnaires',
    category: 'Questionnaires',
    items: [
      // Supplier-side
      {
        audience: 'supplier',
        question: 'How do I fill in a questionnaire?',
        answer: 'Open My Onboarding and click the questionnaire you want to fill. Progress is saved automatically, but you can also click Save Progress at any time. Required questions are marked with an asterisk.',
      },
      {
        audience: 'supplier',
        question: 'Why are some answers already filled in?',
        answer: 'When a questionnaire template is renewed, answers from your most recent approved submission are carried forward and marked "Prefilled". Review each prefilled answer — change anything that is out of date before submitting.',
      },
      {
        audience: 'supplier',
        question: 'What do I do if my customer requests more information?',
        answer: 'You will receive a notification with your customer\'s feedback. Open the questionnaire from My Onboarding, amend the flagged answers, and submit again.',
      },
      // Customer-side
      {
        audience: 'customer',
        question: 'How do I assign a questionnaire to a supplier?',
        answer: 'When you invite a supplier (or reopen their workflow), the default onboarding template is assigned automatically. To add additional questionnaires, open the supplier\'s profile and use the Questionnaires tab.',
      },
      {
        audience: 'customer',
        question: 'How do I build or edit a questionnaire template?',
        answer: 'Open Admin Setup > Questionnaires (or the Templates page, depending on your layout). You can add sections, questions, validation rules and scoring. A new "Latest" version is drafted each time you make changes so active workflows are not disrupted.',
      },
    ],
  },
  {
    slug: 'purchasing',
    category: 'Purchasing',
    items: [
      {
        audience: 'supplier',
        question: 'How do I view my purchase orders?',
        answer: 'Open Purchasing from the sidebar. You see POs your customer has issued to you, along with their lines, schedules and delivery status. Use the filters to narrow by status, date range or customer.',
      },
      {
        audience: 'supplier',
        question: 'How do I acknowledge a purchase order?',
        answer: 'Open the PO from the PO Lines tab and click Acknowledge. This confirms you have accepted the order and agree to the quantities, dates and price.',
      },
      {
        audience: 'supplier',
        question: 'How do I propose changes to a delivery date, quantity, or price?',
        answer: 'In the PO Lines view, open the line you want to change and click Propose Change. Enter your proposed value and a reason — your customer is notified and can accept, reject, or counter-propose.',
      },
      {
        audience: 'customer',
        question: 'Where do I see the POs I have issued?',
        answer: 'Open Purchasing from the sidebar. The PO Headers and PO Lines tabs list everything you have issued. Open any PO to see schedules, acknowledgements and GRNs.',
      },
      {
        audience: 'customer',
        question: 'How do I record a goods receipt?',
        answer: 'Open the PO in the PO Lines tab and click Create GRN, or upload a batch of receipts via Data Import > GRNs. Recorded GRNs feed the OTIF / OTD performance metrics on supplier profiles.',
      },
    ],
  },
  {
    slug: 'supplier_management',
    category: 'Supplier Management',
    items: [
      {
        audience: 'customer',
        question: 'How do I add a new supplier?',
        answer: 'Open Supplier Onboarding > Add Supplier. Supplier records are created when you invite them; the onboarding workflow captures contact, legal and compliance information.',
      },
      {
        audience: 'customer',
        question: 'How do I view supplier performance?',
        answer: `Open the supplier's profile and switch to the Performance tab. OTIF, OTD and ${qualityLabel} appear here once GRNs and NCRs have been recorded against the supplier. If your tenant does not track delivery performance, this tab is hidden.`,
      },
      {
        audience: 'customer',
        question: 'What is the supplier qualification process?',
        answer: 'Qualifications are compliance questionnaires assigned during onboarding (and re-issued at renewal). Track progress on the Supplier Onboarding page or the Questionnaires tab of an individual supplier profile.',
      },
    ],
  },
  {
    slug: 'documents_communication',
    category: 'Documents & Communication',
    items: [
      {
        audience: 'customer',
        question: 'How do I upload a document?',
        answer: 'Open Documents and click Upload Document. Choose whether to keep it private to your organisation, share it with a specific supplier (once enabled), or mark it public. Every download is tracked on the document\'s read-receipt log.',
      },
      {
        audience: 'supplier',
        question: 'Which documents can I see?',
        answer: 'You can see documents you have uploaded, documents scoped to your own company, and any documents your customer has marked public for all suppliers. You cannot see another supplier\'s documents.',
      },
      {
        audience: 'supplier',
        question: 'How do I upload a document my customer needs?',
        answer: 'Use the Documents page to upload the file. Your customer is notified once it appears under your supplier profile.',
      },
      {
        audience: 'customer',
        question: 'How do I create a news announcement?',
        answer: 'Open News & Announcements from the sidebar (admin only) and click Create News. Pick an audience (customer users, suppliers, or both), write the content and publish — the targeted users see the announcement on their dashboard.',
      },
    ],
  },
];

export default function Help() {
  const [searchTerm, setSearchTerm] = useState('');
  const { startTour, setShowQuickReference, hasSeenTour, resetTour } = useTour();
  const { helpLayout } = useMenuSettings();
  const { user } = useAuth();
  const qualityLabel = useQualityMeasureLabel();
  const FAQ_ITEMS = buildFaqItems(qualityLabel);

  // `user.platform` is 'SUPPLIER' for supplier-side users, otherwise they're
  // a customer-side user (or platform admin impersonating a customer).
  const audience: 'customer' | 'supplier' = user?.platform === 'SUPPLIER' ? 'supplier' : 'customer';

  // Filter chain:
  //  1. Category level — the admin superuser can hide whole categories.
  //  2. Item level — each FAQ item is tagged for a customer / supplier / both
  //     audience, so a supplier never sees customer-only "how do I approve a
  //     supplier" and vice versa.
  //  3. Search — typing narrows visible items inside each surviving category.
  //  4. Drop categories that have zero items left after the above.
  const filteredFAQ = FAQ_ITEMS
    .filter(category => helpLayout.faq[category.slug] !== false)
    .map(category => ({
      ...category,
      items: category.items
        .filter(item => item.audience === 'both' || item.audience === audience)
        .filter(
          item =>
            item.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.answer.toLowerCase().includes(searchTerm.toLowerCase())
        ),
    }))
    .filter(category => category.items.length > 0);

  const showTour = helpLayout.sidebar.tour !== false;
  const showContact = helpLayout.sidebar.contact_support !== false;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Help & Support</h1>
        <p className="text-muted-foreground mt-2">
          Find answers to common questions and get help using the platform.
        </p>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative max-w-xl">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search help articles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Main Content Grid */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* FAQ Section */}
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HelpCircle className="h-5 w-5" />
                Frequently Asked Questions
              </CardTitle>
              <CardDescription>
                Browse common questions organized by topic
              </CardDescription>
            </CardHeader>
            <CardContent>
              {filteredFAQ.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  No results found for "{searchTerm}"
                </p>
              ) : (
                <div className="space-y-6">
                  {filteredFAQ.map((category) => (
                    <div key={category.category}>
                      <h3 className="font-semibold text-lg mb-3 text-primary">
                        {category.category}
                      </h3>
                      <Accordion type="single" collapsible className="w-full">
                        {category.items.map((item, index) => (
                          <AccordionItem key={index} value={`${category.category}-${index}`}>
                            <AccordionTrigger className="text-left">
                              {item.question}
                            </AccordionTrigger>
                            <AccordionContent className="text-muted-foreground">
                              {item.answer}
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

        </div>

        {/* Contact Support Sidebar */}
        <div className="space-y-4">
          {/* New to Supplier Portal Card */}
          {showTour && (
          <Card className="border-primary/20 bg-primary/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Rocket className="h-5 w-5 text-primary" />
                New to the Supplier Portal?
              </CardTitle>
              <CardDescription>
                Take a tour or view the navigation guide
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button onClick={startTour} className="w-full">
                <Rocket className="h-4 w-4 mr-2" />
                Start Interactive Tour
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowQuickReference(true)}
                className="w-full"
              >
                <BookOpen className="h-4 w-4 mr-2" />
                Quick Reference Guide
              </Button>
              {hasSeenTour && (
                <Button
                  variant="ghost"
                  onClick={resetTour}
                  className="w-full text-muted-foreground"
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Reset Tour
                </Button>
              )}
            </CardContent>
          </Card>
          )}

          {showContact && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageCircle className="h-5 w-5" />
                Contact Support
              </CardTitle>
              <CardDescription>
                Get in touch with our support team
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">Email Support</p>
                    <a
                      href="mailto:support@valuechain.com"
                      className="text-sm text-primary hover:underline"
                    >
                      support@valuechain.com
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">Phone Support</p>
                    <p className="text-sm text-muted-foreground">Contact your account manager</p>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t">
                <p className="text-sm text-muted-foreground mb-3">
                  Support Hours: Monday - Friday, 9am - 5pm GMT
                </p>
                <a href="mailto:support@valuechain.com" className="w-full">
                  <Button className="w-full">
                    <MessageCircle className="h-4 w-4 mr-2" />
                    Email Support
                  </Button>
                </a>
              </div>
            </CardContent>
          </Card>
          )}

        </div>
      </div>
    </div>
  );
}
